import React, { useEffect } from 'react';
import '../styles/MontierExplainModal.css';

const formatCell = (value, unit) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const n = Number(value);
    if (unit === 'days') return n.toLocaleString('en-IN', { maximumFractionDigits: 1 });
    if (unit === '%') return `${n.toLocaleString('en-IN', { maximumFractionDigits: 1 })}`;
    if (unit === 'Cr') return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    if (unit === 'x') return n.toLocaleString('en-IN', { maximumFractionDigits: 3 });
    return n.toLocaleString('en-IN');
};

const verdictText = (explain) => {
    if (explain?.verdictLabel) return explain.verdictLabel;
    if (explain?.verdict === 'fired') return 'FIRED';
    if (explain?.verdict === 'unavailable') return 'unavailable';
    return 'not fired';
};

const verdictClass = (explain) => {
    const label = (explain?.verdictLabel || '').toLowerCase();
    if (
        explain?.verdict === 'fired'
        || label.includes('high concern')
        || label.includes('quality risk')
        || label.includes('manipulator')
        || label === 'pushes m up'
    ) {
        return 'fired';
    }
    if (explain?.verdict === 'unavailable' || label === 'unavailable') return 'unavailable';
    if (label === 'watch' || label === 'grey' || label === 'caution') return 'watch';
    return 'clear';
};

const formatChange = (pct) => {
    if (pct == null || Number.isNaN(Number(pct))) return '—';
    const n = Number(pct);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
};

export default function ForensicExplainModal({ explain, onClose }) {
    useEffect(() => {
        if (!explain) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [explain, onClose]);

    if (!explain) return null;

    const fromYear = explain.years?.from ? `FY${explain.years.from}` : 'Prior';
    const toYear = explain.years?.to ? `FY${explain.years.to}` : 'Current';
    const inputs = Array.isArray(explain.inputs) ? explain.inputs : [];
    const steps = Array.isArray(explain.steps) ? explain.steps : [];
    const showChange = inputs.some((row) => row.changePct != null);

    return (
        <div
            className="mex-overlay mex-overlay--nested"
            onClick={(event) => {
                event.stopPropagation();
                onClose?.();
            }}
            role="presentation"
        >
            <div
                className="mex-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mex-title"
                onClick={(event) => event.stopPropagation()}
            >
                <header className="mex-header">
                    <div className="mex-title-row">
                        <h2 id="mex-title" className="mex-title">{explain.title}</h2>
                        <button type="button" className="mex-close" onClick={onClose} aria-label="Close">
                            ✕
                        </button>
                    </div>
                    <span className={`mex-verdict mex-verdict--${verdictClass(explain)}`}>
                        {verdictText(explain)}
                    </span>
                </header>

                <div className="mex-body">
                    {explain.formula && (
                        <section className="mex-section">
                            <h3 className="mex-heading">Formula</h3>
                            <pre className="mex-formula">{explain.formula}</pre>
                        </section>
                    )}

                    {inputs.length > 0 && (
                        <section className="mex-section">
                            <h3 className="mex-heading">This company’s numbers</h3>
                            <table className="mex-table">
                                <thead>
                                    <tr>
                                        <th>Line</th>
                                        <th>{fromYear}</th>
                                        <th>{toYear}</th>
                                        {showChange && <th>Change</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {inputs.map((row) => (
                                        <tr key={row.label}>
                                            <td>{row.label}</td>
                                            <td>
                                                {formatCell(row.from, row.unit)}
                                                {row.from != null && row.unit ? ` ${row.unit}` : ''}
                                            </td>
                                            <td>
                                                {formatCell(row.to, row.unit)}
                                                {row.to != null && row.unit ? ` ${row.unit}` : ''}
                                            </td>
                                            {showChange && (
                                                <td className="mex-table__change">{formatChange(row.changePct)}</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {steps.length > 0 && (
                        <section className="mex-section">
                            <h3 className="mex-heading">Calculation</h3>
                            <ol className="mex-steps">
                                {steps.map((step) => (
                                    <li key={step}>{step}</li>
                                ))}
                            </ol>
                        </section>
                    )}

                    {explain.fireRule && (
                        <section className="mex-section">
                            <h3 className="mex-heading">When it fires</h3>
                            <p>{explain.fireRule}</p>
                        </section>
                    )}

                    {explain.verdictWhy && (
                        <section className="mex-section">
                            <h3 className="mex-heading">Why this year</h3>
                            <p>{explain.verdictWhy}</p>
                        </section>
                    )}

                    {explain.whyItMatters && (
                        <section className="mex-section">
                            <h3 className="mex-heading">Why it matters</h3>
                            <p>{explain.whyItMatters}</p>
                        </section>
                    )}

                    {explain.caveat && (
                        <section className="mex-section mex-section--caveat">
                            <h3 className="mex-heading">What it is not</h3>
                            <p>{explain.caveat}</p>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
